import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { ProfileOverviewFacade } from '@app/profile/components/profile-overview-v2/profile-overview.facade';
import { Profile } from '@app/profile/profile.model';
import { WebEnvironmentService } from '@app/shared/services/web-environment.service';
import { ProfileOverviewViewModel } from './profile-overview.model';
import { TagsApi } from '@app/tags/tag-api.model';
import { sortBy } from '@app/shared/utils';
import { TagsService } from '@app/tags/services/tags.service';
import {
  AuthService,
  NotifierService,
  WindowLayoutService,
} from '@dg/shared-services';
import { take } from 'rxjs/operators';
import { ReactiveListVM } from '@dg/shared-rsm';
import { ExperienceItem } from '@app/profile/components/profile-overview/experiences-v2/profile-experience.facade';
import { AchievementItem } from '@app/profile/components/profile-overview/achievements-v2/achievements.facade';
import { LearningItem } from '@app/profile/components/profile-overview/learning-v2/profile-learning.facade';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';

const CONTENT_KEYS = [
  'Core_Empty_Image_Alt',
  'dgProfileOverview_OverviewEmptyStateTitle',
  'dgProfileOverview_OverviewEmptyStateText',
  'dgProfileOverview_OverviewCompleteWizardButton',
  'dgProfileOverview_OverviewSkillsTitle',
  'dgProfileOverview_OverviewExperienceTitle',
  'dgProfileOverview_OverviewAchievementsTitle',
  'dgProfileOverview_OverviewLearningTitle',
  'dgProfileOverview_OverviewNotProvidedTitle',
  'dgProfileOverview_NonOwnerEmptyStateTitle',
];

const ROOT = '/content/img/emptystate';
const SVG_EMPTY_OWNER = `profile-settings`;
const SVG_EMPTY_OVERVIEW = `profile-overview-empty-state`;

const buildLookup =
  (webEnvironmentService: WebEnvironmentService) => (key: string) => {
    return webEnvironmentService.getBlobUrl(`${ROOT}/${key}.svg`, true);
  };

@Component({
  selector: 'dgx-profile-overview-v2',
  templateUrl: './profile-overview.component.html',
  styleUrls: ['./profile-overview.component.scss'],
})
export class ProfileOverviewComponent
  extends SubscriberBaseDirective
  implements OnInit, OnDestroy
{
  @Input() public owner: Profile;
  @Input() public privacyId: number;

  public readonly CLASS_NAME = 'ProfileOverviewComponentV2';
  public i18n = this.translate.instant(CONTENT_KEYS);

  public maxSkills: number = 6;

  public nonOwnerEmptyImageUrl: string;
  public emptyImageUrl: string;

  public isMobile = this.windowLayoutService.isMobile;

  public get nonOwnerEmptyStateText(): string {
    return this.translate.instant('dgProfileOverview_NonOwnerEmptyStateBody', {
      firstName: this.owner.name.split(' ')[0],
    });
  }

  public vm$: Observable<ProfileOverviewViewModel>;
  public readonly experiencesList$: Observable<ReactiveListVM<ExperienceItem>>;
  public readonly achievementsList$: Observable<
    ReactiveListVM<AchievementItem>
  >;
  public readonly learningsList$: Observable<ReactiveListVM<LearningItem>>;

  // Skill suggestions
  public checkForCanonicalTags: boolean = false;
  public isSaving: boolean = false;
  public tags: TagsApi.Tag[] = [];
  public suggestedTags: TagsApi.Tag[] = [];
  public isSuggestedTagsLoading: boolean = false;
  public maxSuggestions = 8;
  public numDefaultInputTagsForSuggestions = 20;
  public currentSuggestedTagIds: number[] = [];
  // the last tag selected via the dropdown, used to generate a new set suggested tags based on this tag
  public currentTag: TagsApi.Tag;

  constructor(
    private facade: ProfileOverviewFacade,
    private router: Router,
    private translate: TranslateService,
    private webEnvironmentService: WebEnvironmentService,
    private authService: AuthService,
    private tagsService: TagsService,
    private notifierService: NotifierService,
    private cdr: ChangeDetectorRef,
    private windowLayoutService: WindowLayoutService
  ) {
    super();
    if (!this.facade.enabled) {
      this.facade.enabled = true;
    }

    this.vm$ = this.facade.vm$;
    this.experiencesList$ = this.facade.experiencesSearch$;
    this.achievementsList$ = this.facade.achievementsSearch$;
    this.learningsList$ = this.facade.learningsSearch$;

    const urlFor = buildLookup(this.webEnvironmentService);
    this.nonOwnerEmptyImageUrl = urlFor(SVG_EMPTY_OWNER);
    this.emptyImageUrl = urlFor(SVG_EMPTY_OVERVIEW);
  }

  public ngOnInit() {
    if (this.authService.isLoggedIn) {
      this.getTagSuggestions();
    }
    this.checkForCanonicalTags =
      this.authService.authUser?.defaultOrgInfo?.settings.enableWorkdaySkillsIntegration;

    this.windowLayoutService.windowResize$
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => {
        this.isMobile = this.windowLayoutService.isMobile;
      });
  }

  public ngOnDestroy() {
    this.facade.enabled = false;
  }

  public goToWizard(): void {
    this.router.navigateByUrl('/getstarted/welcome');
  }

  /**
   * Get the set of suggested tags. If this.currentTag has been set then this tag is used as the input for the suggestions.
   * this.currentTag is set when a user has a selected a tag from the dgx-tags-edit component and not from the suggested tags list.
   * If this.currentTag has not been set - eg, when the modal is first displayed, or the corresponding tag has been
   * removed - then the top 20 tags from the user's tag list are used, based on the latest dateModified values.
   */
  public getTagSuggestions(): void {
    this.isSuggestedTagsLoading = true;
    this.suggestedTags = [];

    const userTags = this.authService.authUser.viewerInterests;

    let topUserTags;

    if (this.currentTag) {
      topUserTags = this.currentTag.name;
    } else {
      // Get the top 20 tags based on dateModified, sorted in descending order if currentTag is not supplied
      topUserTags = userTags
        .sort((a, b) => sortBy(b, a, 'dateModified'))
        .slice(0, this.numDefaultInputTagsForSuggestions)
        .map((t) => t.name)
        .join(',');
    }

    const allTagIds = userTags.map((t) => t.tagId);
    const allTagNames = userTags.map((t) => t.name.toLowerCase());
    this.tagsService
      .getTagSuggestions(topUserTags, this.maxSuggestions)
      .pipe(take(1))
      .subscribe(({ tags, isFromDs3 }) => {
        this.suggestedTags = tags.reduce((allTags, tag) => {
          if (
            !allTagIds.includes(tag.tagId) &&
            !allTagNames.includes(tag.name.toLowerCase())
          ) {
            allTags.push(tag);
          }
          return allTags;
        }, []);
        this.currentSuggestedTagIds = this.suggestedTags.map((t) => t.tagId);
        this.isSuggestedTagsLoading = false;
        this.cdr.detectChanges();
      });
  }

  /**
   * Add a tag from the suggested tags list
   * @param tag - the TagsApi.Tag object that was added from the suggested tags list
   */
  public addSuggestedTag(tag: TagsApi.Tag): void {
    // Prevent duplicate tag items from being added, this can only really happen if someone types in the name of a
    // tag that is also in the suggested tags list, selects it and then tries to add the tag from the suggestions as well
    const isFound = (item: TagsApi.Tag) =>
      item.title.trim().toLowerCase() === tag.title.trim().toLowerCase() ||
      item.name.trim().toLowerCase() === tag.name.trim().toLowerCase();
    const existingTagIndex = this.tags.findIndex(isFound);
    if (existingTagIndex !== -1) {
      // Remove the existing tag
      this.remove(tag);
    } else {
      // Add the new tag
      this.tags = [...this.tags, tag];
      this.save();
    }
  }

  /**
   * Check the selected tags to see if they have canonical workday skills before saving.
   */
  public save() {
    if (this.tags.length === 0) {
      return;
    }

    this.isSaving = true;

    if (this.checkForCanonicalTags) {
      this.saveCanonicalTags();
    } else {
      this.saveTags();
    }
  }

  /**
   * Save the selected tags via the tagsService
   */
  public saveTags(): void {
    this.tagsService.addUserTags(this.tags).subscribe(() => {
      this.isSaving = false;
      this.authService.fetchAuthenticatedUser().subscribe();
      this.notifierService.showSuccess(
        this.translate.instant('dgProfileOverview_AddSkill', {
          skillName: this.tags[this.tags.length - 1].title,
        })
      );
    });
  }

  /**
   *  Look for tags with canonical alternatives and save any that do not
   */
  public saveCanonicalTags(): void {
    this.tagsService.getAllCanonicalTags(this.tags).subscribe((results) => {
      const sorted = this.tags.reduce(
        (acc, curr) => {
          const canonical = results.find((result) => result.tag === curr.name);
          if (
            canonical?.hasCanonical &&
            canonical?.tag !== canonical?.canonicalTag
          ) {
            acc.canonical.push(canonical);
          } else {
            acc.nonCanonical.push(curr);
          }
          return acc;
        },
        { canonical: [], nonCanonical: [] }
      );

      if (sorted.nonCanonical.length) {
        this.tagsService.addUserTags(sorted.nonCanonical).subscribe(() => {
          this.isSaving = false;
          this.authService.fetchAuthenticatedUser().subscribe();
          this.notifierService.showSuccess(
            this.translate.instant('dgProfileOverview_AddSkill', {
              skillName: this.tags[this.tags.length - 1].title,
            })
          );
        });
      } else {
        this.isSaving = false;
        this.authService.fetchAuthenticatedUser().subscribe();
      }
    });
  }

  public remove(tag: TagsApi.Tag) {
    this.isSaving = true;
    this.removeTag(tag);
  }

  public removeTag(tag: TagsApi.Tag): void {
    this.tagsService.removeUserTag(tag).subscribe(() => {
      this.isSaving = false;
      this.authService.fetchAuthenticatedUser().subscribe();
      this.tags = this.tags.filter(
        (t) =>
          t.title.trim().toLowerCase() !== tag.title.trim().toLowerCase() ||
          t.name.trim().toLowerCase() !== tag.name.trim().toLowerCase()
      );
    });
  }
}
